import { type DivIcon, LatLng } from 'leaflet';
import { useEffect, useRef } from 'react';
import { Circle, Marker, useMap, useMapEvents } from 'react-leaflet';
import { getNoddiLeafletIcon } from './markers/getNoddiLeafletIcon';
import {
  clampLatLngToRadius,
  MAP_PIN_RESTRICTION_PATH_OPTIONS,
  MAP_PIN_RESTRICTION_RADIUS_M
} from './pinLocationRestriction';

type Props = {
  centerLat: number;
  centerLng: number;
  radiusMeters?: number;
  /** Override the restriction-center marker (e.g. garage icon). Defaults to the red map pin. */
  centerMarkerIcon?: DivIcon;
};

export const MapPinRestriction = ({
  centerLat,
  centerLng,
  radiusMeters = MAP_PIN_RESTRICTION_RADIUS_M,
  centerMarkerIcon
}: Props) => (
  <>
    <Circle
      center={[centerLat, centerLng]}
      radius={radiusMeters}
      pathOptions={MAP_PIN_RESTRICTION_PATH_OPTIONS}
      interactive={false}
    />
    <MapPinRestrictionFrame centerLat={centerLat} centerLng={centerLng} radiusMeters={radiusMeters} />
    <MapPinRestrictionClamp centerLat={centerLat} centerLng={centerLng} radiusMeters={radiusMeters} />
    <Marker
      position={[centerLat, centerLng]}
      icon={centerMarkerIcon ?? getNoddiLeafletIcon({ redMarker: true })}
      interactive={false}
    />
  </>
);

const MapPinRestrictionFrame = ({
  centerLat,
  centerLng,
  radiusMeters
}: {
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
}) => {
  const map = useMap();

  useEffect(() => {
    const restrictionCenter = new LatLng(centerLat, centerLng);
    const bounds = restrictionCenter.toBounds(radiusMeters * 2);
    // Keep the current map center (pin) after framing so fitBounds does not
    // drag the pin onto the address and maxBounds is intentionally not used —
    // a tight maxBounds + padding makes the viewport larger than the bounds and
    // Leaflet snaps every pan until the user zooms in.
    const pinCenter = clampLatLngToRadius(map.getCenter(), restrictionCenter, radiusMeters);

    map.fitBounds(bounds, { animate: false, padding: [24, 24] });
    map.setView(pinCenter, map.getZoom(), { animate: false });
  }, [centerLat, centerLng, map, radiusMeters]);

  return null;
};

const MapPinRestrictionClamp = ({
  centerLat,
  centerLng,
  radiusMeters
}: {
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
}) => {
  const isClampingRef = useRef(false);

  // Clamp on moveend so we do not cancel an in-progress pan with setView.
  useMapEvents({
    moveend: (event) => {
      if (isClampingRef.current) {
        return;
      }

      const map = event.target;
      const restrictionCenter = new LatLng(centerLat, centerLng);
      const center = map.getCenter();
      const clamped = clampLatLngToRadius(center, restrictionCenter, radiusMeters);

      if (clamped.equals(center)) {
        return;
      }

      isClampingRef.current = true;
      map.setView(clamped, map.getZoom(), { animate: false });
      isClampingRef.current = false;
    }
  });

  return null;
};
